import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

const TONE = {
  default: {
    valueClass: 'text-foreground',
    iconBg: 'rgba(96, 165, 250, 0.10)',
    iconColor: '#60A5FA',
    orb: '#60A5FA',
    progressFrom: 'rgba(96,165,250,0.55)',
    progressTo: 'rgba(96,165,250,0.10)',
  },
  success: {
    valueClass: '',
    valueColor: 'var(--success-fg)',
    iconBg: 'rgba(16, 185, 129, 0.12)',
    iconColor: 'var(--success-fg)',
    orb: '#10B981',
    progressFrom: 'rgba(16,185,129,0.65)',
    progressTo: 'rgba(16,185,129,0.10)',
  },
  warning: {
    valueClass: '',
    valueColor: 'var(--warning-fg)',
    iconBg: 'rgba(245, 158, 11, 0.12)',
    iconColor: 'var(--warning-fg)',
    orb: '#F59E0B',
    progressFrom: 'rgba(245,158,11,0.65)',
    progressTo: 'rgba(245,158,11,0.10)',
  },
  danger: {
    valueClass: '',
    valueColor: 'var(--danger-fg)',
    iconBg: 'rgba(239, 68, 68, 0.12)',
    iconColor: 'var(--danger-fg)',
    orb: '#EF4444',
    progressFrom: 'rgba(239,68,68,0.65)',
    progressTo: 'rgba(239,68,68,0.10)',
  },
  info: {
    valueClass: '',
    valueColor: 'var(--info-fg)',
    iconBg: 'rgba(59, 130, 246, 0.12)',
    iconColor: 'var(--info-fg)',
    orb: '#3B82F6',
    progressFrom: 'rgba(59,130,246,0.65)',
    progressTo: 'rgba(59,130,246,0.10)',
  },
  accent: {
    valueClass: 'text-gold-gradient',
    iconBg: 'rgba(212, 168, 67, 0.14)',
    iconColor: '#D4A843',
    orb: '#D4A843',
    progressFrom: '#D4A843',
    progressTo: 'rgba(212,168,67,0.10)',
  },
};

function useCountUp(target, duration = 700) {
  const [v, setV] = useState(0);
  const animated = useRef(false);
  useEffect(() => {
    if (typeof target !== 'number' || target <= 0) {
      setV(typeof target === 'number' ? target : 0);
      return;
    }
    if (animated.current) { setV(target); return; }
    animated.current = true;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

export default function KpiCard({
  label, value, icon: Icon, tone = 'default', suffix, testid, highlight, delta, index = 0,
}) {
  const t = TONE[tone] || TONE.default;
  const numeric = typeof value === 'number';
  const animated = useCountUp(numeric && value < 10000 ? value : 0);
  const display = numeric ? (value < 10000 ? animated : value) : value;
  const progressPct = numeric ? Math.min(100, Math.max(8, value === 0 ? 8 : 60)) : 70;

  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden premium-card premium-card-hover animate-fade-slide-up"
      style={{
        padding: '22px 24px',
        animationDelay: `${index * 60}ms`,
        ...(highlight ? { borderTop: '2px solid rgba(212,168,67,0.50)' } : {}),
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -top-3 -right-3 h-20 w-20 rounded-full"
        style={{ background: t.orb, opacity: 0.08, filter: 'blur(20px)' }}
      />

      <div className="flex items-center justify-between mb-4 relative">
        <span className="text-[10px] font-semibold uppercase tracking-[0.10em] text-muted-foreground/70">
          {label}
        </span>
        {Icon && (
          <div
            className="flex h-9 w-9 items-center justify-center rounded-[10px] shrink-0"
            style={{ background: t.iconBg }}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} style={{ color: t.iconColor }} />
          </div>
        )}
      </div>

      <div className="relative">
        <div
          className={cn(
            'leading-none num',
            tone === 'accent' && highlight ? 'text-[22px] font-extrabold text-gold-gradient' : 'text-[32px] font-extrabold',
            !t.valueColor && (t.valueClass || 'text-foreground'),
          )}
          style={{
            color: tone === 'accent' && highlight ? undefined : t.valueColor,
            letterSpacing: '-0.04em',
          }}
        >
          {display}
          {suffix && (
            <span className="ml-1 text-sm font-medium text-muted-foreground">{suffix}</span>
          )}
        </div>

        <div
          className="mt-4 h-[3px] w-full overflow-hidden rounded-full"
          style={{ background: 'var(--border-subtle)' }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${t.progressFrom}, ${t.progressTo})`,
              transition: 'width 700ms cubic-bezier(0.16,1,0.3,1)',
            }}
          />
        </div>

        {delta !== undefined && delta !== null && (
          <div className="mt-2 flex justify-end">
            <span
              className="text-[11px] font-semibold"
              style={{
                color:
                  delta > 0 ? 'var(--success-fg)' : delta < 0 ? 'var(--danger-fg)' : 'var(--neutral-fg)',
              }}
            >
              {delta > 0 ? '↑' : delta < 0 ? '↓' : '—'} {Math.abs(delta)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
